import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiUpload, FiFileText, FiCheck, FiX, FiChevronRight, FiAlertTriangle } from 'react-icons/fi';
import Modal from './common/Modal';
import api from '../services/api';

const SHIPMENT_TYPES = [
  { value: 'ocean-import', label: 'Ocean Import' },
  { value: 'ocean-export', label: 'Ocean Export' },
  { value: 'air-import', label: 'Air Import' },
  { value: 'truck', label: 'Truck' },
];

/**
 * ImportShipmentModal - supports two modes:
 *  1. Create new shipment (default) - when no existingShipment prop
 *  2. Update existing shipment - when existingShipment = { id, type } is provided
 *     In update mode, only empty fields get filled; conflicts are shown for user choice
 */
const ImportShipmentModal = ({ isOpen, onClose, defaultType, existingShipment, onUpdated }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [step, setStep] = useState(1); // 1=upload, 2=review, 3=creating/updating
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [shipmentType, setShipmentType] = useState(defaultType || 'ocean-import');
  const [editableData, setEditableData] = useState({});

  // Sync shipmentType when defaultType prop changes (e.g., opening modal from different pages)
  React.useEffect(() => {
    if (defaultType) {
      setShipmentType(defaultType);
    }
  }, [defaultType]);

  // Reset all state when modal opens so stale data from previous upload is cleared
  React.useEffect(() => {
    if (isOpen) {
      setStep(1);
      setFile(null);
      setParsing(false);
      setParsedData(null);
      setEditableData({});
      setUpdatePreview(null);
      setSelectedOverrides({});
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [isOpen]);

  // Update mode state
  const isUpdateMode = !!existingShipment;
  const [updatePreview, setUpdatePreview] = useState(null); // { updates, conflicts, additions, newContainers, newHBL }
  const [selectedOverrides, setSelectedOverrides] = useState({}); // field -> true/false (user picks which conflicts to override)

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const ext = selectedFile.name.split('.').pop().toLowerCase();
    if (!['pdf', 'xls', 'xlsx'].includes(ext)) {
      toast.error('Please select a PDF or Excel (.xls) file');
      return;
    }

    setFile(selectedFile);
    setParsing(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      if (isUpdateMode) {
        // Update mode: parse and compare against existing shipment
        const res = await api.post(
          `/import/parse-update/${existingShipment.id}?type=${existingShipment.type}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
        );

        if (res.data.success) {
          setParsedData(res.data);
          setUpdatePreview({
            updates: res.data.updates || {},
            conflicts: res.data.conflicts || [],
            additions: res.data.additions || [],
            newContainers: res.data.newContainers || [],
            containerUpdates: res.data.containerUpdates || [],
            newHBL: res.data.newHBL || null,
          });
          // Initialize all overrides as unchecked
          const overrideInit = {};
          (res.data.conflicts || []).forEach(c => { overrideInit[c.field] = false; });
          setSelectedOverrides(overrideInit);
          setStep(2);
        } else {
          toast.error(res.data.error || 'Failed to parse file');
        }
      } else {
        // Create mode: original behavior
        const res = await api.post('/import/parse', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000, // 2 min for OCR processing
        });

        if (res.data.success) {
          setParsedData(res.data.data);
          setEditableData(res.data.data);
          setStep(2);

          if (res.data.data.file_type === 'ISF' || res.data.data.file_type === 'SCANNED_DOC' || res.data.data.mbl_no) {
            setShipmentType('ocean-import');
          }
        } else {
          toast.error(res.data.error || 'Failed to parse file');
        }
      }
    } catch (error) {
      const msg = error.response?.data?.error || 'Failed to parse file';
      toast.error(msg);
    } finally {
      setParsing(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditableData(prev => ({ ...prev, [field]: value }));
  };

  const toggleOverride = (field) => {
    setSelectedOverrides(prev => ({ ...prev, [field]: !prev[field] }));
  };

  // Apply updates to existing shipment
  const handleApplyUpdate = async () => {
    setStep(3);
    try {
      const overrides = {};
      updatePreview.conflicts.forEach(c => {
        if (selectedOverrides[c.field]) {
          overrides[c.field] = c.newValue;
        }
      });

      const res = await api.post(
        `/import/apply-update/${existingShipment.id}?type=${existingShipment.type}`,
        {
          updates: updatePreview.updates,
          overrides,
          newContainers: updatePreview.newContainers,
          containerUpdates: updatePreview.containerUpdates || [],
          newHBL: updatePreview.newHBL,
        }
      );

      if (res.data.success) {
        const addCount = Object.keys(updatePreview.updates).length;
        const overrideCount = Object.keys(overrides).length;
        const ctCount = updatePreview.newContainers.length;
        const cuCount = (updatePreview.containerUpdates || []).length;
        const parts = [];
        if (addCount) parts.push(`${addCount} fields filled`);
        if (overrideCount) parts.push(`${overrideCount} fields updated`);
        if (ctCount) parts.push(`${ctCount} containers added`);
        if (cuCount) parts.push(`${cuCount} containers updated`);
        if (updatePreview.newHBL) parts.push('HBL added');
        toast.success(`Shipment updated: ${parts.join(', ') || 'no changes'}`);
        onClose();
        if (onUpdated) onUpdated(res.data.shipment);
      } else {
        toast.error('Failed to update shipment');
        setStep(2);
      }
    } catch (error) {
      console.error('Apply update error:', error);
      toast.error(error.response?.data?.error || 'Failed to update shipment');
      setStep(2);
    }
  };

  const handleCreate = async () => {
    setStep(3);
    try {
      // Use defaultType prop directly to ensure correct shipment type from page context
      const activeType = defaultType || shipmentType || 'ocean-import';
      const shipmentData = mapToShipmentType(editableData, activeType);
      const endpoint = `/${activeType}`;
      const res = await api.post(endpoint, shipmentData);

      if (res.data && res.data.id) {
        toast.success(`Shipment created successfully from ${parsedData.file_type} file`);

        if (activeType === 'ocean-import' && editableData.hbl_no) {
          try {
            await api.post(`/ocean-import/${res.data.id}/hbl`, {
              hbl_no: editableData.hbl_no,
              shipper: editableData.shipper || '',
              consignee: editableData.consignee || '',
              notify_party: editableData.notify_party || '',
              description: editableData.description || editableData.memo || '',
              package_count: editableData.package_count || '',
              package_type: editableData.package_type || '',
              weight_kg: editableData.weight_kg || '',
              measurement_cbm: editableData.measurement_cbm || '',
            });
          } catch (e) {
            console.warn('Failed to create HBL:', e);
          }
        }

        if (editableData.containers && editableData.containers.length > 0) {
          for (const ct of editableData.containers) {
            if (ct.container_no) {
              try {
                let pkgType = ct.pkg_type || '';
                if (pkgType) {
                  const upper = pkgType.toUpperCase();
                  if (upper.startsWith('CTN') || upper.startsWith('CARTON')) pkgType = 'CARTON(S)';
                  else if (upper.startsWith('PKG') || upper.startsWith('PACKAGE')) pkgType = 'PACKAGE(S)';
                  else if (upper === 'PCS' || upper === 'PIECES') pkgType = 'PIECES';
                  else if (upper.startsWith('BOX')) pkgType = 'BOXES';
                  else if (upper.startsWith('PALL')) pkgType = 'PALLET(S)';
                }

                await api.post(`/containers/shipment/${res.data.id}`, {
                  container_no: ct.container_no,
                  seal_no: ct.seal_no || '',
                  tp_sz: ct.tp_sz || '',
                  pkg_type: pkgType || 'CARTON(S)',
                  pkg_count: ct.quantity ? parseFloat(ct.quantity) : null,
                  weight_kg: ct.weight_kg ? parseFloat(ct.weight_kg) : null,
                  measurement_cbm: ct.cbm ? parseFloat(ct.cbm) : null,
                  shipment_type: activeType,
                });
              } catch (e) {
                console.warn('Failed to create container:', e);
              }
            }
          }
        }

        onClose();
        navigate(`/${activeType}/${res.data.id}`);
      } else {
        toast.error('Failed to create shipment');
        setStep(2);
      }
    } catch (error) {
      console.error('Create shipment error:', error);
      toast.error(error.response?.data?.error || 'Failed to create shipment');
      setStep(2);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (!droppedFile) return;
    const ext = droppedFile.name.split('.').pop().toLowerCase();
    if (!['pdf', 'xls', 'xlsx'].includes(ext)) {
      toast.error('Please drop a PDF or Excel (.xls) file');
      return;
    }
    // Trigger the same flow as file select
    // Create a synthetic event-like object and call handleFileSelect
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(droppedFile);
    fileInputRef.current.files = dataTransfer.files;
    fileInputRef.current.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleReset = () => {
    setStep(1);
    setFile(null);
    setParsedData(null);
    setEditableData({});
    setUpdatePreview(null);
    setSelectedOverrides({});
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const mapToShipmentType = (data, type) => {
    const today = new Date().toISOString().split('T')[0];
    const cargoFields = {
      package_type: data.package_type || '',
      package_count: data.package_count || '',
      weight_kg: data.weight_kg || '',
      measurement_cbm: data.measurement_cbm || '',
    };
    const descField = { description: data.description || data.memo || '' };
    const freightPickup = data.freight_location
      ? (data.firms_code ? data.firms_code + ' ' : '') + data.freight_location
      : '';

    if (type === 'ocean-import') {
      return {
        mbl_no: data.mbl_no || '', vessel_flight_no: data.vessel_flight_no || '',
        port_of_loading: data.port_of_loading || '', port_of_discharge: data.port_of_discharge || '',
        final_destination: data.final_destination || '', etd: data.etd || '', eta: data.eta || '',
        shipper: data.shipper || '', consignee: data.consignee || '',
        notify_party: data.notify_party || '', customer: data.customer || '', memo: data.memo || '',
        carrier: data.carrier || '', customs_broker: data.customs_broker || '',
        freight_location: data.freight_location || '', firms_code: data.firms_code || '',
        entry_no: data.entry_no || '', service_type: data.service_type || '',
        freight_terms: data.freight_terms || '', trade_terms: data.trade_terms || '',
        forwarding_agent: data.forwarding_agent || '', freight_pickup: freightPickup,
        ...cargoFields, ...descField, post_date: today, status: 'Pending',
      };
    }
    if (type === 'ocean-export') {
      return {
        mbl_no: data.mbl_no || '', vessel_flight_no: data.vessel_flight_no || '',
        port_of_loading: data.port_of_loading || '', port_of_discharge: data.port_of_discharge || '',
        final_destination: data.final_destination || '', etd: data.etd || '', eta: data.eta || '',
        shipper: data.shipper || '', consignee: data.consignee || '', customer: data.customer || '',
        memo: data.memo || '', ...cargoFields, ...descField, post_date: today, status: 'Pending',
      };
    }
    if (type === 'air-import') {
      return {
        mawb_no: data.mbl_no || '', flight_no: data.vessel_flight_no || '',
        departure: data.port_of_loading || '', destination: data.port_of_discharge || '',
        departure_date_time: data.etd || '', arrival_date_time: data.eta || '',
        shipper: data.shipper || '', consignee: data.consignee || '', customer: data.customer || '',
        carrier: data.carrier || '', oversea_agent: data.forwarding_agent || '',
        memo: data.memo || '',
        package_count: data.package_count || '', package_type: data.package_type || '',
        gross_weight_kg: data.weight_kg || '', measurement_cbm: data.measurement_cbm || '',
        ...descField, post_date: today, status: 'Pending',
      };
    }
    if (type === 'truck') {
      return {
        mbl_no: data.mbl_no || '', hbl_no: data.hbl_no || '', vessel_flight_no: data.vessel_flight_no || '',
        port_of_loading: data.port_of_loading || '', port_of_discharge: data.port_of_discharge || '',
        final_destination: data.final_destination || '', etd: data.etd || '', eta: data.eta || '',
        shipper: data.shipper || '', consignee: data.consignee || '', customer: data.customer || '',
        notify_party: data.notify_party || '', memo: data.memo || '', freight_pickup: freightPickup,
        ...cargoFields, ...descField, post_date: today, status: 'Pending',
      };
    }
    return data;
  };

  // Field definitions - dynamic labels for air vs ocean
  const isAirImport = shipmentType === 'air-import' || shipmentType === 'air_import' ||
    (parsedData && parsedData.file_type === 'AWB');
  const CORE_FIELDS = [
    { key: 'mbl_no', label: isAirImport ? 'MAWB No.' : 'MB/L No.' },
    { key: 'hbl_no', label: isAirImport ? 'HAWB No.' : 'HB/L No.' },
    { key: 'vessel_flight_no', label: isAirImport ? 'Flight No.' : 'Vessel / Flight' },
    { key: 'port_of_loading', label: 'Port of Loading' },
    { key: 'port_of_discharge', label: 'Port of Discharge' },
    { key: 'final_destination', label: 'Final Destination' },
    { key: 'etd', label: 'ETD', type: 'date' },
    { key: 'eta', label: 'ETA', type: 'date' },
    { key: 'shipper', label: 'Shipper' },
    { key: 'consignee', label: 'Consignee' },
    { key: 'notify_party', label: 'Notify Party' },
    { key: 'customer', label: 'Customer' },
  ];
  const EXTENDED_FIELDS = [
    { key: 'carrier', label: 'Carrier' },
    { key: 'customs_broker', label: 'Customs Broker' },
    { key: 'freight_location', label: 'Freight Location' },
    { key: 'firms_code', label: 'FIRMS Code' },
    { key: 'entry_no', label: 'Entry No.' },
    { key: 'service_type', label: 'Service Type' },
    { key: 'freight_terms', label: 'Freight Terms' },
    { key: 'trade_terms', label: 'Trade Terms' },
    { key: 'forwarding_agent', label: 'Forwarding Agent' },
    { key: 'invoice_no', label: 'Invoice / Ref No.' },
    { key: 'release_date', label: 'Release Date' },
  ];
  const CARGO_FIELDS = [
    { key: 'package_count', label: 'Package Count' },
    { key: 'package_type', label: 'Package Type' },
    { key: 'weight_kg', label: 'Weight (KG)' },
    { key: 'measurement_cbm', label: 'CBM' },
    { key: 'memo', label: 'Description / Memo' },
  ];
  const REVIEW_FIELDS = [...CORE_FIELDS, ...EXTENDED_FIELDS, ...CARGO_FIELDS];

  const renderUploadStep = () => (
    <div style={{ textAlign: 'center', padding: '30px 20px' }}>
      {isUpdateMode && (
        <div style={{
          padding: '10px 14px', background: '#eff6ff', borderRadius: '8px',
          marginBottom: '16px', fontSize: '13px', color: '#1e40af', textAlign: 'left',
        }}>
          Uploading document for existing shipment. The system will compare the extracted data
          and only fill in fields that are currently empty. If there are conflicts, you can choose
          which values to keep.
        </div>
      )}
      <div
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        style={{
          border: `2px dashed ${dragging ? '#2563eb' : '#cbd5e1'}`,
          borderRadius: '12px', padding: '40px 20px',
          cursor: 'pointer', transition: 'all 0.2s',
          background: dragging ? '#eff6ff' : '#f8fafc',
        }}
        onMouseEnter={e => { if (!dragging) { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.background = '#eff6ff'; }}}
        onMouseLeave={e => { if (!dragging) { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.background = '#f8fafc'; }}}
      >
        <FiUpload style={{ fontSize: '40px', color: dragging ? '#2563eb' : '#64748b', marginBottom: '12px' }} />
        <div style={{ fontSize: '16px', fontWeight: 600, color: '#334155', marginBottom: '8px' }}>
          {parsing ? 'Parsing file...' : dragging ? 'Drop file here' : isUpdateMode ? 'Click or drag to upload additional document' : 'Click or drag to upload shipment document'}
        </div>
        <div style={{ fontSize: '13px', color: '#94a3b8' }}>
          Supports: ISF (.xls), Bill of Lading (.pdf), Scanned Documents (DO/AN/BOL/Packing List)
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.xls,.xlsx"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      {parsing && (
        <div style={{ marginTop: '20px', color: '#2563eb', fontSize: '14px' }}>
          <div className="spinner" style={{ display: 'inline-block', marginRight: '8px' }} />
          {isUpdateMode ? 'Parsing and comparing with existing data...' : 'Extracting data from file...'}
        </div>
      )}
    </div>
  );

  // Update mode review - shows what will be added/changed
  const renderUpdateReview = () => {
    if (!updatePreview) return null;
    const { updates, conflicts, additions, newContainers, containerUpdates = [], newHBL } = updatePreview;
    const hasChanges = Object.keys(updates).length > 0 || conflicts.length > 0 || newContainers.length > 0 || containerUpdates.length > 0 || newHBL;

    return (
      <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
        {/* File info */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '10px 14px', background: '#f0fdf4', borderRadius: '8px',
          marginBottom: '16px', fontSize: '13px', color: '#166534',
        }}>
          <FiFileText />
          <span><strong>{file?.name}</strong> - {parsedData?.file_type === 'SCANNED_DOC' ? 'Scanned document (OCR)' : parsedData?.file_type} parsed successfully</span>
        </div>

        {/* Detected pages */}
        {parsedData?.detected_pages && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '8px 14px', background: '#eff6ff', borderRadius: '8px',
            marginBottom: '10px', fontSize: '12px', color: '#1e40af', flexWrap: 'wrap',
          }}>
            <span style={{ fontWeight: 600 }}>Detected pages:</span>
            {parsedData.detected_pages.map((p, i) => (
              <span key={i} style={{
                padding: '2px 8px', background: '#dbeafe', borderRadius: '4px', fontSize: '11px',
              }}>
                P{p.page}: {p.type}
              </span>
            ))}
          </div>
        )}

        {!hasChanges && (
          <div style={{
            padding: '20px', textAlign: 'center', color: '#64748b', fontSize: '14px',
          }}>
            No new data found to update. All fields in this shipment already have values.
          </div>
        )}

        {/* Fields to be auto-filled (empty -> new value) */}
        {additions.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{
              fontWeight: 600, color: '#166534', marginBottom: '8px', fontSize: '14px',
              display: 'flex', alignItems: 'center', gap: '6px',
            }}>
              <FiCheck style={{ color: '#16a34a' }} />
              Fields to be filled ({additions.length}):
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <tbody>
                {additions.map(item => (
                  <tr key={item.field} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{
                      padding: '8px 10px', fontWeight: 600, color: '#64748b',
                      width: '140px', fontSize: '12px',
                    }}>
                      {item.label}
                    </td>
                    <td style={{ padding: '6px 4px' }}>
                      <span style={{
                        color: '#16a34a', fontWeight: 500, background: '#f0fdf4',
                        padding: '2px 8px', borderRadius: '4px',
                      }}>
                        {item.newValue}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Conflicts - user can choose to override */}
        {conflicts.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{
              fontWeight: 600, color: '#b45309', marginBottom: '8px', fontSize: '14px',
              display: 'flex', alignItems: 'center', gap: '6px',
            }}>
              <FiAlertTriangle style={{ color: '#f59e0b' }} />
              Conflicts - check to override ({conflicts.length}):
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <tbody>
                {conflicts.map(item => (
                  <tr key={item.field} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px 4px', width: '30px', verticalAlign: 'top' }}>
                      <input
                        type="checkbox"
                        checked={selectedOverrides[item.field] || false}
                        onChange={() => toggleOverride(item.field)}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{
                      padding: '8px 10px', fontWeight: 600, color: '#64748b',
                      width: '120px', fontSize: '12px', verticalAlign: 'top',
                    }}>
                      {item.label}
                    </td>
                    <td style={{ padding: '6px 4px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ fontSize: '12px' }}>
                          <span style={{ color: '#94a3b8' }}>Current: </span>
                          <span style={{ color: '#334155' }}>{item.oldValue}</span>
                        </div>
                        <div style={{ fontSize: '12px' }}>
                          <span style={{ color: '#94a3b8' }}>New: </span>
                          <span style={{
                            color: selectedOverrides[item.field] ? '#16a34a' : '#64748b',
                            fontWeight: selectedOverrides[item.field] ? 600 : 400,
                            textDecoration: selectedOverrides[item.field] ? 'none' : 'none',
                          }}>
                            {item.newValue}
                          </span>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* New containers */}
        {newContainers.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{
              fontWeight: 600, color: '#166534', marginBottom: '6px', fontSize: '14px',
              display: 'flex', alignItems: 'center', gap: '6px',
            }}>
              <FiCheck style={{ color: '#16a34a' }} />
              New containers to add ({newContainers.length}):
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#f0fdf4', textAlign: 'left' }}>
                  <th style={{ padding: '4px 8px', fontWeight: 600, color: '#166534' }}>Container No.</th>
                  <th style={{ padding: '4px 8px', fontWeight: 600, color: '#166534' }}>Size</th>
                  <th style={{ padding: '4px 8px', fontWeight: 600, color: '#166534' }}>Qty</th>
                  <th style={{ padding: '4px 8px', fontWeight: 600, color: '#166534' }}>Weight</th>
                </tr>
              </thead>
              <tbody>
                {newContainers.map((ct, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '4px 8px', fontWeight: 600 }}>{ct.container_no}</td>
                    <td style={{ padding: '4px 8px' }}>{ct.tp_sz || '-'}</td>
                    <td style={{ padding: '4px 8px' }}>{ct.quantity || '-'}</td>
                    <td style={{ padding: '4px 8px' }}>{ct.weight_kg || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Container updates */}
        {containerUpdates.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{
              fontWeight: 600, color: '#1e40af', marginBottom: '6px', fontSize: '14px',
              display: 'flex', alignItems: 'center', gap: '6px',
            }}>
              <FiCheck style={{ color: '#2563eb' }} />
              Containers to update ({containerUpdates.length}):
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#eff6ff', textAlign: 'left' }}>
                  <th style={{ padding: '4px 8px', fontWeight: 600, color: '#1e40af' }}>Container No.</th>
                  <th style={{ padding: '4px 8px', fontWeight: 600, color: '#1e40af' }}>Field</th>
                  <th style={{ padding: '4px 8px', fontWeight: 600, color: '#1e40af' }}>New Value</th>
                </tr>
              </thead>
              <tbody>
                {containerUpdates.map((cu, i) => (
                  Object.entries(cu.updates).map(([field, value], j) => (
                    <tr key={`${i}-${j}`} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      {j === 0 && <td style={{ padding: '4px 8px', fontWeight: 600 }} rowSpan={Object.keys(cu.updates).length}>{cu.container_no}</td>}
                      <td style={{ padding: '4px 8px' }}>{field.replace(/_/g, ' ')}</td>
                      <td style={{ padding: '4px 8px', color: '#2563eb', fontWeight: 500 }}>{value}</td>
                    </tr>
                  ))
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* New HBL */}
        {newHBL && (
          <div style={{
            padding: '10px 14px', background: '#f0fdf4', borderRadius: '8px',
            fontSize: '13px', color: '#166534', display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            <FiCheck style={{ color: '#16a34a' }} />
            <span>New HB/L to add: <strong>{typeof newHBL === 'string' ? newHBL : newHBL.hbl_no}</strong></span>
          </div>
        )}
      </div>
    );
  };

  // Create mode review (original)
  const renderCreateReview = () => (
    <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '10px 14px', background: '#f0fdf4', borderRadius: '8px',
        marginBottom: '16px', fontSize: '13px', color: '#166534'
      }}>
        <FiFileText />
        <span><strong>{file?.name}</strong> - {parsedData?.file_type === 'SCANNED_DOC' ? 'Scanned document (OCR)' : parsedData?.file_type} parsed successfully</span>
      </div>

      {parsedData?.detected_pages && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '8px 14px', background: '#eff6ff', borderRadius: '8px',
          marginBottom: '10px', fontSize: '12px', color: '#1e40af', flexWrap: 'wrap',
        }}>
          <span style={{ fontWeight: 600 }}>Detected pages:</span>
          {parsedData.detected_pages.map((p, i) => (
            <span key={i} style={{
              padding: '2px 8px', background: '#dbeafe', borderRadius: '4px', fontSize: '11px',
            }}>
              P{p.page}: {p.type}
            </span>
          ))}
        </div>
      )}

      {/* Shipment type is determined by the page context (defaultType prop) */}

      <div style={{ fontSize: '13px' }}>
        <div style={{ fontWeight: 600, color: '#475569', marginBottom: '8px', fontSize: '14px' }}>
          Extracted Data (edit if needed):
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {REVIEW_FIELDS.map(field => {
              const value = editableData[field.key] || '';
              if (!value && !['mbl_no', 'vessel_flight_no', 'port_of_loading', 'port_of_discharge', 'shipper', 'consignee'].includes(field.key)) {
                return null;
              }
              return (
                <tr key={field.key} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{
                    padding: '8px 10px', fontWeight: 600, color: '#64748b',
                    width: '140px', verticalAlign: 'top', fontSize: '12px',
                  }}>
                    {field.label}
                  </td>
                  <td style={{ padding: '6px 4px' }}>
                    {field.key === 'memo' ? (
                      <textarea
                        value={value}
                        onChange={e => handleFieldChange(field.key, e.target.value)}
                        rows={2}
                        style={{
                          width: '100%', padding: '6px 8px', border: '1px solid #e2e8f0',
                          borderRadius: '4px', fontSize: '13px', resize: 'vertical',
                        }}
                      />
                    ) : (
                      <input
                        type={field.type === 'date' ? 'date' : 'text'}
                        value={value}
                        onChange={e => handleFieldChange(field.key, e.target.value)}
                        style={{
                          width: '100%', padding: '6px 8px', border: '1px solid #e2e8f0',
                          borderRadius: '4px', fontSize: '13px',
                        }}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {editableData.containers && editableData.containers.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            <div style={{ fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
              Containers ({editableData.containers.length}):
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
                  <th style={{ padding: '4px 8px', fontWeight: 600, color: '#475569' }}>Container No.</th>
                  <th style={{ padding: '4px 8px', fontWeight: 600, color: '#475569' }}>Seal No.</th>
                  <th style={{ padding: '4px 8px', fontWeight: 600, color: '#475569' }}>Size</th>
                  <th style={{ padding: '4px 8px', fontWeight: 600, color: '#475569' }}>Qty</th>
                  <th style={{ padding: '4px 8px', fontWeight: 600, color: '#475569' }}>Weight (KG)</th>
                  <th style={{ padding: '4px 8px', fontWeight: 600, color: '#475569' }}>CBM</th>
                </tr>
              </thead>
              <tbody>
                {editableData.containers.map((ct, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '4px 8px', fontWeight: 600 }}>{ct.container_no}</td>
                    <td style={{ padding: '4px 8px', color: '#64748b' }}>{ct.seal_no || '-'}</td>
                    <td style={{ padding: '4px 8px', color: '#64748b' }}>{ct.tp_sz || ''}</td>
                    <td style={{ padding: '4px 8px', color: '#64748b' }}>{ct.quantity || '-'}</td>
                    <td style={{ padding: '4px 8px', color: '#64748b' }}>{ct.weight_kg || '-'}</td>
                    <td style={{ padding: '4px 8px', color: '#64748b' }}>{ct.cbm || ct.measurement_cbm || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderFooter = () => {
    if (step === 1) return null;

    if (step === 3) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#2563eb' }}>
          <div className="spinner" style={{ width: '16px', height: '16px' }} />
          {isUpdateMode ? 'Updating shipment...' : 'Creating shipment...'}
        </div>
      );
    }

    if (isUpdateMode) {
      const hasChanges = updatePreview && (
        Object.keys(updatePreview.updates).length > 0 ||
        updatePreview.conflicts.some(c => selectedOverrides[c.field]) ||
        updatePreview.newContainers.length > 0 ||
        (updatePreview.containerUpdates || []).length > 0 ||
        updatePreview.newHBL
      );

      return (
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', width: '100%' }}>
          <button className="btn" onClick={handleReset} style={{ marginRight: 'auto' }}>
            <FiUpload /> Upload Different File
          </button>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleApplyUpdate} disabled={!hasChanges}>
            <FiCheck /> Apply Updates
          </button>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', width: '100%' }}>
        <button className="btn" onClick={handleReset} style={{ marginRight: 'auto' }}>
          <FiUpload /> Upload Different File
        </button>
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button className="btn btn-primary" onClick={handleCreate}>
          <FiCheck /> Create Shipment
        </button>
      </div>
    );
  };

  const getTitle = () => {
    if (step === 1) return isUpdateMode ? 'Upload Document to Update Shipment' : 'Import Shipment from File';
    if (step === 2) return isUpdateMode ? 'Review Updates' : 'Review Extracted Data';
    return isUpdateMode ? 'Updating Shipment...' : 'Creating Shipment...';
  };

  return (
    <Modal
      isOpen={isOpen}
      title={getTitle()}
      onClose={onClose}
      footer={renderFooter()}
    >
      {step === 1 && renderUploadStep()}
      {step >= 2 && (isUpdateMode ? renderUpdateReview() : renderCreateReview())}
    </Modal>
  );
};

export default ImportShipmentModal;
